/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import DataGridButton from "../../components/DataGridButton";
import { Box, Grid, Modal, Tooltip, Typography, useTheme } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-pro";
import { useListClusters, useGetCluster } from "../../api/clusters";
import { dataGridButtonTypography } from "../../styles/DataGridStyles";
import {
  dataGridButtonStyles,
  iamDividerStyles,
} from "../../styles/buttonStyles";
import { tabbedDataGridPage } from "../../styles/commonPageStyles";
import { Info, Sync, Visibility } from "@mui/icons-material";
import YAML from "yaml";
import useIamModal from "../IamModal/UseIamModal";
import DataTable from "../../components/DataTable";
import StatusChip from "../../components/StatusChip";
import { styles as clustersStyles } from "./styles";

const styles = {
  // Column layout so the grid takes every pixel left under the header row,
  // matching the Tessell GCP control planes page.
  mainBox: {
    ...tabbedDataGridPage.tabContentMainBox,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
  headerGrid: { ...tabbedDataGridPage.gridContainer, flexShrink: 0 },
  dataTable: { flex: 1, minHeight: 0 },
};

const Clusters = () => {
  const theme = useTheme();
  const colors = tokens(theme.paletteId);
  const { deploymentStage } = useParams();

  const [clusters, setClusters] = useState([]);
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const {
    postData: listClusters,
    response: listClustersResp,
    isLoading: isLoadingClusters,
  } = useListClusters(deploymentStage);

  const {
    postData: getCluster,
    response: getClusterResp,
    responseStatus: getClusterStatus,
    error: getClusterError,
    isLoading: isLoadingCluster,
  } = useGetCluster(deploymentStage, selectedCluster?.deploymentName);

  // The hook's response/error/status persist across calls, so without this
  // check a failed or still-in-flight fetch for a newly selected cluster
  // would keep showing the previously viewed cluster's data.
  const clusterDetails =
    selectedCluster?.deploymentName &&
    getClusterResp?.deploymentName === selectedCluster.deploymentName
      ? getClusterResp
      : null;
  const clusterDetailsFailed =
    getClusterError ||
    (typeof getClusterStatus === "number" && getClusterStatus !== 200);

  const { IamButton, IamModal, canViewIamModal } = useIamModal(
    `/apps/tessell/deployment-stages/${deploymentStage}/k8s-clusters`
  );

  useEffect(() => {
    listClusters();
  }, [deploymentStage]);

  useEffect(() => {
    if (Array.isArray(listClustersResp?.clusters)) {
      setClusters(listClustersResp.clusters);
    } else {
      setClusters([]);
    }
  }, [listClustersResp]);

  const handleViewCluster = (clusterName) => {
    setSelectedCluster({ deploymentName: clusterName });
    setModalOpen(true);
  };

  useEffect(() => {
    if (selectedCluster && modalOpen) {
      getCluster();
    }
  }, [selectedCluster, modalOpen]);

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedCluster(null);
  };

  const columns = [
    {
      field: "deploymentName",
      renderHeader: () => "Cluster Name",
      flex: 1,
      cellClassName: "name-column--cell",
      renderCell: ({ row: { deploymentName } }) => deploymentName || "",
    },
    {
      field: "displayName",
      flex: 1,
      renderHeader: () => "Display Name",
      renderCell: ({ row: { displayName } }) => displayName || "",
    },
    {
      field: "cloudName",
      flex: 0.7,
      renderHeader: () => "Cloud",
      renderCell: ({ row: { cloudName } }) => cloudName || "",
    },
    {
      field: "cloudRegion",
      flex: 0.7,
      renderHeader: () => "Region",
      renderCell: ({ row: { cloudRegion } }) => cloudRegion || "",
    },
    {
      field: "releaseLabel",
      flex: 1,
      renderHeader: () => "Release Label",
      renderCell: ({ row: { releaseLabel, releaseManifestOverride } }) => (
        <>
          {releaseLabel ?? ""}
          {releaseManifestOverride ? (
            <Tooltip
              title={
                <Typography variant="body2" sx={clustersStyles.RmoTypography}>
                  {YAML.stringify({ RMO: releaseManifestOverride }, null, 2)}
                </Typography>
              }
            >
              <Info fontSize="small" />
            </Tooltip>
          ) : (
            <></>
          )}
        </>
      ),
    },
    {
      field: "status",
      flex: 0.8,
      renderHeader: () => "Status",
      renderCell: ({ row: { status } }) => <StatusChip value={status} />,
    },
    {
      field: "actions",
      type: "actions",
      flex: 0.5,
      getActions: ({ row }) => [
        <GridActionsCellItem
          icon={<Visibility />}
          label="View Details"
          onClick={() => handleViewCluster(row.deploymentName)}
        />,
      ],
    },
  ];

  return (
    <Box sx={styles.mainBox}>
      {IamModal}
      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        aria-labelledby="cluster-modal-title"
        aria-describedby="cluster-modal-description"
      >
        <Box sx={clustersStyles.modalBox(colors)}>
          <Typography
            id="cluster-modal-title"
            variant="h4"
            component="h2"
            mb={2}
          >
            Cluster Details: {selectedCluster?.deploymentName}
          </Typography>
          {isLoadingCluster ? (
            <Typography variant="h5">Loading...</Typography>
          ) : clusterDetailsFailed ? (
            <Typography variant="h5">Failed to load cluster details</Typography>
          ) : clusterDetails ? (
            <Box sx={clustersStyles.modalContent}>
              <Typography variant="body1" component="pre">
                {YAML.stringify(clusterDetails, null, 2)}
              </Typography>
            </Box>
          ) : (
            <Typography variant="h5">No data available</Typography>
          )}
        </Box>
      </Modal>
      <Grid sx={styles.headerGrid} container columns={2}>
        <Grid xs={1}>
          <Header
            title="Kubernetes Clusters"
            subtitle={`See the list of ${deploymentStage} clusters`}
          />
        </Grid>
        <Grid xs={1}>
          <Box sx={{ ...tabbedDataGridPage.topButtonStack, gap: "8px" }}>
            {IamButton}
            {canViewIamModal ? <Box sx={iamDividerStyles(colors)} /> : null}
            <DataGridButton
              sx={dataGridButtonStyles.pageTopButtons}
              onClick={() => listClusters()}
            >
              <Sync sx={{ fontSize: 16, color: "#fff" }} />
              <Typography sx={dataGridButtonTypography(colors, 500)}>
                RELOAD
              </Typography>
            </DataGridButton>
          </Box>
        </Grid>
      </Grid>
      {/* Keyed on the stage for the same reason as the control planes page:
          the router keeps this mounted when only :deploymentStage changes,
          so without a key the grid carries its filters between stages. */}
      <DataTable
        key={deploymentStage}
        hideFooter
        height="100%"
        sx={styles.dataTable}
        getRowId={(row) => row?.deploymentName}
        loading={isLoadingClusters}
        rows={clusters}
        columns={columns}
      />
    </Box>
  );
};

export default Clusters;
